import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLanguage } from '../../i18n/useLanguage';
import { formatMessage } from '../../i18n/LanguageContext';
import useSchool from './hooks/useSchool';
import CreateSchoolModal from './components/CreateSchoolModal';
import ImportSchoolsModal from './components/ImportSchoolsModal';
import LocationDisplay from './components/LocationDisplay';
import { schoolService } from '../../services/schoolService';
import type { SchoolCreateRequest } from './types';
import { getSchoolLevelLabel } from './constants';
import AdminListSortSelect from '../../components/ui/AdminListSortSelect';

const SchoolDashboard: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { t: translate } = useLanguage();

  const {
    // Data
    schools,
    pagination,
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    updateFilters,
    updatePagination,
    resetFilters,
    refetch,

    // Mutations
    createSchool,
    deleteSchool,
    toggleStatus,

    // Mutation states
    isCreating,
    isDeleting,
    isTogglingStatus,

    // Mutation objects for error handling
    createSchoolMutation,
    deleteSchoolMutation,
    toggleStatusMutation,
  } = useSchool();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Hoạt động';
      case 'inactive':
        return 'Không hoạt động';
      case 'pending':
        return 'Chờ duyệt';
      default:
        return status || 'Chưa có trạng thái';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'public':
        return 'Trường Công lập';
      case 'private':
        return 'Trường Tư thục';
      case 'international':
        return 'Trường Quốc tế';
      case 'vocational':
        return 'Trường Nghề';
      default:
        return type;
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFilters({ search: e.target.value });
  };

  const handleTypeFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value === 'all' ? '' : e.target.value;
    updateFilters({ type: value as any });
  };

  const handleStatusFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value === 'all' ? '' : e.target.value;
    updateFilters({ status: value as any });
  };

  const handleCreateSchool = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateSchoolSubmit = async (data: SchoolCreateRequest) => {
    try {
      await createSchoolMutation.mutateAsync(data);
      toast.success(formatMessage(translate('school.create.success'), { name: data.name }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || translate('school.create.error'));
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleImportSchools = async (schools: SchoolCreateRequest[]) => {
    try {
      const result = await schoolService.bulkImportSchools(schools);
      
      if (result.success) {
        const { success_count, error_count } = result.data;
        
        if (error_count > 0) {
          toast.success(`Import thành công ${success_count} trường học. ${error_count} trường học có lỗi.`);
        } else {
          toast.success(`Import thành công ${success_count} trường học.`);
        }
        
        // Refresh the schools list
        refetch();
      } else {
        toast.error('Import thất bại');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Lỗi khi import dữ liệu');
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleDeleteSchool = (schoolId: string, schoolName: string) => {
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">⚠️</span>
          <span className="font-medium">Xác nhận xóa trường học</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn xóa trường học <strong>"{schoolName}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                deleteSchoolMutation.mutateAsync(schoolId),
                {
                  loading: translate('school.delete.loading'),
                  success: formatMessage(translate('school.delete.success'), { name: schoolName }),
                  error: (err) => err?.response?.data?.message || translate('school.delete.error'),
                }
              );
            }}
            className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
          >
            Xóa
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleToggleStatus = (schoolId: string, schoolName: string, currentStatus: string) => {
    const isActive = currentStatus === 'active';
    const nextStatus = isActive ? 'inactive' : 'active';
    const actionText = isActive ? 'tạm dừng' : 'kích hoạt';
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{isActive ? '⏸️' : '▶️'}</span>
          <span className="font-medium">Xác nhận {actionText} trường học</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn {actionText} trường học <strong>"{schoolName}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                toggleStatusMutation.mutateAsync({ id: schoolId, status: nextStatus }),
                {
                  loading: `Đang ${actionText} trường học...`,
                  success: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} trường học "${schoolName}" thành công!`,
                  error: (err) => err?.response?.data?.message || `Có lỗi xảy ra khi ${actionText} trường học`,
                }
              );
            }}
            className={`px-3 py-1 text-sm text-white rounded transition-colors ${
              isActive 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {actionText.charAt(0).toUpperCase() + actionText.slice(1)}
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };





  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Trường học</h1>
          <p className="text-gray-600 mt-1">
            Quản lý danh sách các trường học, thông tin và hiệu suất đào tạo
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleCreateSchool}
            disabled={isCreating}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isCreating ? 'Đang tạo...' : '+ Thêm trường học mới'}
          </button>
          <button 
            onClick={() => setIsImportModalOpen(true)}
            disabled={isCreating}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Import từ Excel
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Tìm kiếm theo tên trường, địa điểm hoặc loại hình..."
                value={filters.search || ''}
                onChange={handleSearch}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <select 
              value={filters.type || 'all'} 
              onChange={handleTypeFilter}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tất cả loại hình</option>
              <option value="public">Trường Công lập</option>
              <option value="private">Trường Tư thục</option>
              <option value="international">Trường Quốc tế</option>
              <option value="vocational">Trường Nghề</option>
            </select>
            <select 
              value={filters.status || 'all'} 
              onChange={handleStatusFilter}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="active">Hoạt động</option>
              <option value="inactive">Không hoạt động</option>
              <option value="pending">Chờ duyệt</option>
            </select>
            <AdminListSortSelect
              value={filters.sort}
              onChange={(value) => updateFilters({ sort: value })}
              disabled={isLoading}
            />
            <button
              onClick={() => {
                resetFilters();
                toast.success(translate('school.filters.resetSuccess'), {
                  icon: '🔄',
                  duration: 2000,
                });
              }}
              className="px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex justify-between items-start">
            <div>
              <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-1"></div>
              <div className="h-4 w-80 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-10 w-40 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>

          {/* Search and Filters Skeleton */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
              <div className="flex gap-3">
                <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-36 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-24 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
            </div>
          </div>



          {/* Schools List Skeleton */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <th key={i} className="px-6 py-3 text-left">
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
                          <div className="ml-4">
                            <div className="h-4 w-32 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-24 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}



      {/* Schools List */}
      {!isLoading && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Danh sách Trường học</h2>
            {isFetching && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Đang cập nhật...
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TÊN TRƯỜNG
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MÃ TRƯỜNG
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LOẠI HÌNH
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CẤP ĐỘ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VỊ TRÍ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HỌC VIÊN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GIẢNG VIÊN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LỚP HỌC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRẠNG THÁI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HÀNH ĐỘNG
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isError ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="text-red-500">
                          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-center">
                          <h3 className="text-lg font-medium text-red-600 mb-2">Lỗi tải dữ liệu</h3>
                          <p className="text-gray-600 mb-4">{error?.message || translate('school.list.error')}</p>
                          <button 
                            onClick={() => {
                              toast.promise(
                                refetch(),
                                {
                                  loading: translate('school.list.reloadLoading'),
                                  success: 'Tải dữ liệu thành công!',
                                  error: 'Không thể tải dữ liệu',
                                }
                              );
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Thử lại
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : schools.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                      Không tìm thấy trường học nào
                    </td>
                  </tr>
                ) : (
                  schools.map((school) => (
                    <tr key={school.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4">
                            <Link
                              to={`/admin/school/${school.id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                            >
                              {school.name}
                            </Link>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{school.code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{getTypeText(school.type)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{getSchoolLevelLabel(school.level)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <LocationDisplay
                          district={school.district}
                          city={school.city}
                          districtCode={school.districtCode}
                          cityCode={school.cityCode}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{school.studentCount?.toLocaleString() || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{school.teacherCount || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{school.classCount || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(school.status)}`}>
                          {getStatusText(school.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link
                            to={`/admin/school/${school.id}`}
                            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Xem chi tiết"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          <button 
                            onClick={() => handleToggleStatus(school.id, school.name, school.status)}
                            disabled={isTogglingStatus}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                              school.status === 'active' 
                                ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={school.status === 'active' ? 'Tạm dừng hoạt động' : 'Kích hoạt hoạt động'}
                          >
                            {school.status === 'active' ? (
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </button>
                          <button 
                            onClick={() => handleDeleteSchool(school.id, school.name)}
                            disabled={isDeleting}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Xóa trường học"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Hiển thị {((page - 1) * per_page) + 1} đến {Math.min(page * per_page, pagination.total)} 
                  trong tổng số {pagination.total} trường học
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => updatePagination(page - 1)}
                    disabled={page <= 1}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Trước
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Trang {page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => updatePagination(page + 1)}
                    disabled={page >= pagination.totalPages}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sau
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create School Modal */}
      <CreateSchoolModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSchoolSubmit}
        isCreating={isCreating}
      />

      {/* Import Schools Modal */}
      <ImportSchoolsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportSchools}
        isImporting={isCreating}
      />
    </div>
  );
};

export default SchoolDashboard;
